import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fullUpdate, selectSeason, updateFilters } from '@store/slice/sterilizationSlice'
import { get } from '@util/xhrAPI'
import { useTranslation } from 'react-i18next'
import Alert from '@ui/elements/Alert'
import Div from '@ui/layout/Div'
import Buttons from '@ui/elements/Buttons'
import SterilizationsListTable from '@scene/window/sterilization/__partials/SterilizationListTable'
import SterilizationsListMap from '@scene/window/sterilization/__partials/SterilizationListMap'
import TextType from '@ui/inputs/TextType'
import SterilizationListCommands from '@scene/window/sterilization/__partials/SterilizationListCommands'
import SterilizationListFilters from '@scene/window/sterilization/__partials/SterilizationListFilters'
import ScansData from '@scene/window/sterilization/__partials/ScansData'
import DateRangeType from '../../../ui/inputs/DateRangeType'

const SterilizationsList = () => {
  const [viewType, setViewType] = useState('grid')
  const [search, setSearch] = useState('')
  const { calls, activeFilters, selectedSeason, seasonsScansData } = useSelector(state => state.sterilization)
  const { mission } = useSelector(state => state.main)
  const { missionSeasons, activeSeason } = mission
  const dispatch = useDispatch()
  const [t] = useTranslation('common')

  const getNodes = () => {
    const nodes = Object.values(calls[selectedSeason]).reverse()
    return nodes.filter(call => {
      const address = `${call.companyName},${call.sterilizationDistrict}, ${call.sterilizationAddress},${call.sterilizationZipCode}`
      return call.addressResponsibleFullName.toLowerCase().includes(search.toLowerCase())
        || address.toLowerCase().includes(search.toLowerCase())
        || call.id == search
    })
  }

  const dataSterilizationCalls = { nodes: !calls[selectedSeason] ? [] : getNodes() }

  const getCalls = (isClear = false) => {
    let query = ''
    if (!isClear) {
      for (let field in activeFilters) {
        if (null !== activeFilters[field]) {
          query = `${query}&${field}=${activeFilters[field]}`
        }
      }
    }

    get(`${import.meta.env.VITE_API_URL}/api/missions/${mission.id}/mission-seasons/${selectedSeason}/calls${query ? `?${query}` : ''}`, response => {
      const { code, sterilizationCalls, filters, scansData } = response
      if (code === 200) {
        dispatch(
          fullUpdate({
            calls: { ...calls, [selectedSeason]: { ...sterilizationCalls } },
            filters,
            seasonsScansData: { ...seasonsScansData, [selectedSeason]: { ...scansData } }
          })
        )
      }
    })
  }

  const handleSearchField = (event) => {
    setSearch(event.target.value)
  }

  const getSeasons = () => {
    const seasonsButtons = Object.keys(missionSeasons).map(season => {
      return {
        cssClass: `is-rounded ${selectedSeason == season ? 'is-success' : ''}`,
        disabled: selectedSeason === parseInt(season),
        callback: () => dispatch(selectSeason(parseInt(season))),
        text: missionSeasons[season]
      }
    })
    return <Buttons buttons={seasonsButtons} />
  }

  useEffect(() => {
    dispatch(selectSeason(selectedSeason || activeSeason.id))
    if ('undefined' === typeof calls[selectedSeason] && selectedSeason) {
      getCalls()
    }
  }, [selectedSeason])

  return (
    <>
      <Div cssClass='calls-list'>
        <SterilizationListCommands />
        <h2 className='zone-title'>{t('titles.sterilization_calls_list')}</h2>
        <Div cssClass='seasons-zone'>
          <h3>{t('titles.seasons')}</h3>
          {getSeasons()}
        </Div>
        <SterilizationListFilters getCalls={getCalls} nbAddresses={dataSterilizationCalls.nodes.length} />
        <ScansData />
        <Div cssClass='columns is-multiline filters-inputs'>
          <TextType value={search || ''} onChange={handleSearchField} placeholder={t('labels.search_field_sterilization_calls')} cssClass={'column is-8'} />
          <Div cssClass='column is-4'>
            <Div cssClass='columns date-filter'>
              <DateRangeType
                name={'filter-dateRange'}
                onChange={(name, value) => dispatch(updateFilters({ ...activeFilters, ...value }))}
                onClear={getCalls}
                cssClass='column is-10'
              />
              <Buttons buttons={{
                icon: 'icon-filter_list_alt',
                cssClass: 'is-success is-small validate-date-filter',
                callback: () => getCalls()
              }} cssClass='column' />
            </Div>
          </Div>
        </Div>

        {null !== calls && dataSterilizationCalls.nodes.length
          ? <>
            <Div cssClass='data-show'>
              <Div cssClass='view-switcher'>
                <Buttons buttons={[
                  {
                    icon: 'icon-grid',
                    cssClass: 'is-default is-rounded',
                    callback: () => setViewType('grid'),
                    disabled: viewType === 'grid'
                  }, {
                    icon: 'icon-map',
                    cssClass: 'is-default is-rounded',
                    callback: () => setViewType('map'),
                    disabled: viewType === 'map'
                  }
                ]}
                  addons={true}
                />
              </Div>
              {viewType === 'grid'
                ? <SterilizationsListTable dataSterilizationCalls={dataSterilizationCalls} />
                : <SterilizationsListMap dataSterilizationCalls={dataSterilizationCalls} />
              }
            </Div>
          </>
          : <Alert type='is-info' message={t('messages.empty_list')} />
        }
      </Div>
    </>
  )
}

export default SterilizationsList